import React, { useEffect, useState, useCallback } from "react";
import { useUser } from "../../context/UserContext";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import MultiPhotoCapture from "./MultiPhotoCapture";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import Button from "../UI/Button";
import actionLabels from "../../config/actionLabels";
import Modal from '../UI/Modal';

const schema = yup.object().shape({
    username: yup.string().required("Имя обязательно"),
    position: yup.string().required("Должность обязательна"),
    department: yup.string().nullable(),
    shiftSchedule: yup.string().required("Смена обязательна"),
    password: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .min(6, "Мин. 6 символов")
        .notRequired(),
});

const UsersList = () => {
    const { user, logout, hasPermission } = useUser();
    const canEdit = hasPermission("users", "edit");
    const canDelete = hasPermission("users", "delete");
    const canView = hasPermission("users", "view");

    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [positions, setPositions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [photoData, setPhotoData] = useState(null);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        setValue,
    } = useForm({ resolver: yupResolver(schema) });

    const fetchUsers = useCallback(async () => {
        const params = new URLSearchParams({
            search,
            page,
            limit,
        });
        const res = await fetchWithAuth(
            `${process.env.REACT_APP_API_URL}/admin/users?${params.toString()}`,
            {},
            logout
        );
        const data = await res.json();
        setUsers(data.users);
        setTotal(data.total);
    }, [search, page, limit, user.token]);

    const fetchPositions = async () => {
        const res = await fetchWithAuth(
            `${process.env.REACT_APP_API_URL}/positions`,
            {},
            logout
        );
        const data = await res.json();
        setPositions(data);
    };

    useEffect(() => {
        fetchPositions();
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [positionsRes, departmentsRes, shiftsRes] = await Promise.all([
                    fetchWithAuth(`${process.env.REACT_APP_API_URL}/positions`, {}, logout),
                    fetchWithAuth(`${process.env.REACT_APP_API_URL}/admin/departments`, {}, logout),
                    fetchWithAuth(`${process.env.REACT_APP_API_URL}/admin/shift-schedules`, {}, logout)
                ]);
                const positionsData = await positionsRes.json();
                const departmentsData = await departmentsRes.json();
                const shiftsData = await shiftsRes.json();
                setPositions(positionsData);
                setDepartments(departmentsData);
                setShifts(shiftsData);
            } catch (err) {
                console.error("Ошибка при получении данных:", err);
            }
        };

        fetchData();
    }, []);

    const totalPages = Math.ceil(total / limit);

    if (!canView) {
        return (
            <div className="text-center text-2xl text-red-500">
                У вас нет доступа к просмотру пользователей
            </div>
        );
    }

    const handleDelete = async (id) => {
        if (!window.confirm("Удалить пользователя?")) return;
        await fetchWithAuth(
            `${process.env.REACT_APP_API_URL}/admin/users/${id}`,
            {
                method: "DELETE",
            },
            logout
        );
        fetchUsers();
    };

    const openEditModal = (u) => {
        setEditingUser(u);
        setValue("username", u.username || "");
        setValue("fullName", u.fullName || "");
        setValue("firstName", u.firstName || "");
        setValue("lastName", u.lastName || "");
        setValue("position", u.position?.name || "");
        setValue("department", u.department?._id || "");
        setValue("shiftSchedule", u.shiftSchedule?._id || "");
        setValue("password", "");
    };

    const handleUpdate = async (data) => {
        const formData = new FormData();
        formData.append("username", data.username);
        formData.append("fullName", data.fullName);
        formData.append("firstName", data.firstName);
        formData.append("lastName", data.lastName);
        formData.append("position", data.position);
        formData.append("department", data.department);
        formData.append("shiftSchedule", data.shiftSchedule);
        if (data.password) formData.append("password", data.password);

        if (photoData && Array.isArray(photoData)) {
            photoData.forEach((file) => {
                formData.append("photos", file);
            });
        }

        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/users/${editingUser._id}`,
                {
                    method: "PUT",
                    body: formData,
                },
                logout
            );

            const result = await response.json();

            setEditingUser(null);
            fetchUsers();
            reset();
            setPhotoData(null);
        } catch (error) {
            console.error("❌ Ошибка при обновлении:", error);
        }
    };

    return (
        <div>
            <div className="flex gap-4 mb-4">
                <input
                    type="text"
                    placeholder="Поиск по имени"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="px-3 py-2 bg-[#1E1E1E] border border-[#3E3E42] rounded"
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full table-auto border border-[#3E3E42]">
                    <thead>
                        <tr className="bg-[#252526]">
                            <th className="p-2 border border-[#3E3E42]">
                                Фото
                            </th>
                            <th className="p-2 border border-[#3E3E42]">
                                Фамилмя
                            </th>
                            <th className="p-2 border border-[#3E3E42]">Имя</th>
                            <th className="p-2 border border-[#3E3E42]">
                                Отчество
                            </th>
                            <th className="p-2 border border-[#3E3E42]">
                                Логин
                            </th>
                            <th className="p-2 border border-[#3E3E42]">
                                Должность
                            </th>
                            <th className="p-2 border border-[#3E3E42]">
                                Отдел
                            </th>
                            <th className="p-2 border border-[#3E3E42]">
                                Смена
                            </th>
                            <th className="p-2 border border-[#3E3E42]">
                                Дата
                            </th>
                            <th className="p-2 border border-[#3E3E42]">
                                Действия
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u._id} className="text-sm">
                                <td className="p-2 border border-[#3E3E42]">
                                    {u.photoPath && (
                                        <img
                                            src={`${process.env.REACT_APP_API_URL}${u.photoPath}`}
                                            alt="Фото"
                                            className="w-12 h-12 object-cover rounded"
                                        />
                                    )}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {u.lastName}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {u.fullName}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {u.firstName}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {u.username}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {u.position?.name}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {u.department?.name}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {u.shiftSchedule?.name} ({u.shiftSchedule?.startTime} - {u.shiftSchedule?.endTime})
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {new Date(u.createdAt).toLocaleString()}
                                </td>
                                <td className="px-2 py-[0.8rem] border border-[#3E3E42] flex gap-2">
                                    {canEdit && (
                                        <Button
                                            onClick={() => openEditModal(u)}
                                            variant="edit"
                                        >
                                            Ред.
                                        </Button>
                                    )}
                                    {canDelete && (
                                        <Button
                                            onClick={() => handleDelete(u._id)}
                                            variant="danger"
                                        >
                                            Удалить
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => (
                    <Button
                        key={i}
                        onClick={() => setPage(i + 1)}
                        variant="pagination"
                        className={
                            page === i + 1 ? "bg-[#3E3E42] text-white" : ""
                        }
                    >
                        {i + 1}
                    </Button>
                ))}
            </div>

            <Modal
                isOpen={!!editingUser}
                onClose={() => {
                    setEditingUser(null);
                    reset();
                    setPhotoData(null);
                }}
                title="Редактировать пользователя"
            >
                <form
                    onSubmit={handleSubmit(handleUpdate)}
                    className="space-y-4"
                >
                    <h2 className="text-lg font-semibold">
                        Редактировать пользователя
                    </h2>
                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                        {...register("lastName")}
                        placeholder="Фамилия"
                    />
                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                        {...register("fullName")}
                        placeholder="Имя"
                    />
                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                        {...register("firstName")}
                        placeholder="Отчество"
                    />
                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                        {...register("username")}
                        placeholder="Логин"
                    />
                    {errors.username && (
                        <p className="text-red-500 text-sm">
                            {errors.username.message}
                        </p>
                    )}

                    <select
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                        {...register("position")}
                    >
                        <option value="" disabled>
                            Выберите должность...
                        </option>
                        {positions.map((p) => (
                            <option key={p._id} value={p.name}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                    {errors.position && (
                        <p className="text-red-500 text-sm">
                            {errors.position.message}
                        </p>
                    )}

                    <div className="space-y-2">
                        <select
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                            {...register("department")}
                        >
                            <option value="" disabled>
                                Выберите отдел...
                            </option>
                            {departments.map((dept) => (
                                <option key={dept._id} value={dept._id}>
                                    {dept.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-2">
                        <select
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                            {...register("shiftSchedule")}
                        >
                            <option value="" disabled>Выберите смену...</option>
                            {shifts.map((shift) => (
                                <option key={shift._id} value={shift._id}>
                                    {shift.name} ({shift.startTime} - {shift.endTime})
                                </option>
                            ))}
                        </select>
                    </div>
                    {errors.shiftSchedule && (
                        <p className="text-red-500 text-sm">
                            {errors.shiftSchedule.message}
                        </p>
                    )}

                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                        {...register("password")}
                        placeholder="Новый пароль (необязательно)"
                        type="password"
                    />
                    {errors.password && (
                        <p className="text-red-500 text-sm">
                            {errors.password.message}
                        </p>
                    )}

                    <MultiPhotoCapture onCaptureComplete={setPhotoData} />

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            onClick={() => setEditingUser(null)}
                            variant="cancel"
                        >
                            Отмена
                        </Button>
                        <Button type="submit" variant="primary">
                            Сохранить
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default UsersList;
